<template>
    <div class="user-details">
        <form-wrapper
            class="uk-form-stacked"
            :validator="$v.buffer.user"
            :messages="messagesOverride"
        >
            <div
                uk-grid
                class="uk-grid-small"
            >
                <auto-complete-field
                    v-if="!withoutEmail"
                    v-model="buffer.user.email"
                    placeholder="Email"
                    label="Email"
                    :hide-label="true"
                    name="email"
                    :v="$v.buffer.user.email"
                    :auto-complete-func="getEmailAddresses"
                    :results="emailAddresses"
                    class="uk-width-1-1"
                    @input="enableUserDetailRetrieval"
                    @onInputBlur="retrieveUserDetails"
                />
                <checkbox-field
                    v-if="!isEditMode && !isUserDetailRetrieved"
                    id="withoutEmail"
                    v-model="withoutEmail"
                    label="Customer doesn't have an email"
                    name="withoutEmail"
                    :show-error-on-blur-only="true"
                    @change="updateWithoutEmailFlag"
                />
            </div>
        </form-wrapper>
        <form-wrapper
            v-if="!isEditMode && showIsPrimary"
            class="uk-form-stacked"
            :validator="$v.buffer.isPrimary"
        >
            <div
                uk-grid
                class="uk-grid-small"
            >
                <checkbox-field
                    id="isPrimaryCheckbox"
                    v-model="buffer.isPrimary"
                    label="Set as Primary"
                    name="isPrimary"
                    :show-error-on-blur-only="true"
                    @change="updateIsPrimary"
                />
            </div>
        </form-wrapper>
        <form-wrapper
            class="uk-form-stacked"
            :validator="$v.buffer.user"
        >
            <div
                uk-grid
                class="uk-grid-small"
            >
                <select-field
                    v-model="buffer.user.title"
                    placeholder="Title"
                    label="Title"
                    :hide-label="true"
                    name="title"
                    :options="sortedTitles"
                    class="uk-width-1-3@s"
                    :v="$v.buffer.user.title"
                    :show-error-on-blur-only="true"
                    @input="updateInput"
                />
                <text-field
                    v-model="buffer.user.firstName"
                    placeholder="First Name"
                    label="First Name"
                    :hide-label="true"
                    name="firstName"
                    class="uk-width-1-3@s"
                    :v="$v.buffer.user.firstName"
                    :show-error-on-blur-only="true"
                    @input="updateInput"
                />
                <text-field
                    v-model="buffer.user.surname"
                    placeholder="Surname"
                    label="Surname"
                    :hide-label="true"
                    name="surname"
                    class="uk-width-1-3@s"
                    :v="$v.buffer.user.surname"
                    :show-error-on-blur-only="true"
                    @input="updateInput"
                />
                <div>
                    <p class="uk-text-small">
                        Must be the customer's legal name as per their license or passport.
                    </p>
                </div>
            </div>
            <div
                uk-grid
                class="uk-grid-small"
            >
                <telephone-field
                    v-model="buffer.user.mobileNumber"
                    placeholder="Mobile Number"
                    label="Mobile Number"
                    :hide-label="true"
                    name="mobileNumber"
                    class="uk-width-1-1"
                    :v="$v.buffer.user.mobileNumber"
                    :show-error-on-blur-only="true"
                    @input="updateInput"
                />
            </div>
        </form-wrapper>
        <h4>
            Billing Address
        </h4>
        <form-wrapper
            class="uk-form-stacked"
            :validator="$v.buffer.address"
        >
            <div
                uk-grid
                class="uk-grid-small"
            >
                <text-field
                    v-model="buffer.address.address1"
                    placeholder="Address Line 1"
                    label="Address Line 1"
                    :hide-label="true"
                    name="address1"
                    class="uk-width-1-1@s"
                    :v="$v.buffer.address.address1"
                    :show-error-on-blur-only="true"
                    @input="updateInput"
                />
            </div>
            <div
                uk-grid
                class="uk-grid-small"
            >
                <text-field
                    v-model="buffer.address.address2"
                    placeholder="Address Line 2"
                    label="Address Line 2"
                    :hide-label="true"
                    name="address2"
                    class="uk-width-1-1@s"
                    :v="$v.buffer.address.address2"
                    :show-error-on-blur-only="true"
                    @input="updateInput"
                />
            </div>
            <div
                uk-grid
                class="uk-grid-small"
            >
                <text-field
                    v-model="buffer.address.suburb"
                    placeholder="Suburb"
                    label="Suburb"
                    :hide-label="true"
                    name="suburb"
                    class="uk-width-1-3@s"
                    :v="$v.buffer.address.suburb"
                    :show-error-on-blur-only="true"
                    @input="updateInput"
                />
                <select-field
                    v-model="buffer.address.state"
                    placeholder="State"
                    label="State"
                    :hide-label="true"
                    name="state"
                    :options="sortedStates"
                    :enable-null-option="true"
                    class="uk-width-1-3@s"
                    :v="$v.buffer.address.state"
                    :show-error-on-blur-only="true"
                    @input="updateInput"
                />
                <numeric-field
                    v-model="buffer.address.postcode"
                    placeholder="Postcode"
                    label="Postcode"
                    :hide-label="true"
                    name="postcode"
                    class="uk-width-1-3@s"
                    :v="$v.buffer.address.postcode"
                    :size="4"
                    :show-error-on-blur-only="true"
                    @input="updateInput"
                />
            </div>
        </form-wrapper>
    </div>
</template>

<script>

import { email, integer, required } from "vuelidate/lib/validators";
import { cloneObject, copyData, sortByKey, isSimondsEmail } from '@strangeanimals/common-vue-components/src/helpers/helpers';
import { createDummyCustomerEmail } from "../../helpers/helpers";
import { getUserDetailsByEmailRequest, getEmailAddressesRequest } from '../../helpers/api';

const NumericField = () => import("@strangeanimals/common-vue-components/src/components/fields/NumericField");
const TelephoneField = () => import("@strangeanimals/common-vue-components/src/components/fields/TelephoneField.vue");
const TextField  = () => import( "@strangeanimals/common-vue-components/src/components/fields/TextField");
const SelectField  = () => import( "@strangeanimals/common-vue-components/src/components/fields/SelectField");
const AutoCompleteField  = () => import( "@strangeanimals/common-vue-components/src/components/fields/AutoCompleteField");
const CheckboxField  = () => import( "@strangeanimals/common-vue-components/src/components/fields/CheckboxField");

const notSimondsEmail = (value, vm) => (!(isSimondsEmail(value)));

export default {
    components: { CheckboxField, TextField, NumericField, AutoCompleteField, SelectField, TelephoneField },
    props: {
        mode: {
            type: String,
            default: () => {
                return "create";
            }
        },
        enableValidation: {
            type: Boolean,
            default: () => {
                return true;
            }
        },
        shouldJobDetailBeRetrieved: {
            type: Boolean,
            default: () => {
                return true;
            }
        },
        userDetails: {
            type: Object,
            default: () => {
                return {
                    user: {
                        title: '',
                        firstName: '',
                        surname: '',
                        mobileNumber: '',
                        email: '',
                    },
                    address: {
                        address1: '',
                        address2: '',
                        suburb: '',
                        state: '',
                        postcode: '',
                    },
                    isPrimary: false
                };
            }
        },
        isUserDetailRetrieved: {
            type: Boolean,
            default: () => {
                return false;
            }
        },
        titles: {
            type: Object,
            default: () => {
                return {};
            }
        },
        states: {
            type: Object,
            default: () => {
                return {};
            }
        },
        isCreditCardPayment: {
            type: Boolean,
            default: () => {
                return false;
            }
        },
        showIsPrimary: {
            type: Boolean,
            default: () => {
                return true;
            }
        }
    },
    data: function () {
        return {
            buffer: cloneObject(this.userDetails),
            bufferIsUserDetailRetrieved: this.isUserDetailRetrieved,
            emailAddresses: [],
            withoutEmail: false,
            messagesOverride: {
                notSimondsEmail: "Simonds email cannot be used here."
            },
        };
    },
    computed: {
        isEditMode: function () {
            return this.mode === "edit";
        },
        sortedTitles: function () {
            return sortByKey(this.titles);
        },
        sortedStates: function () {
            return sortByKey(this.states);
        },
        isFormValid: function () {
            return (this.bufferIsUserDetailRetrieved || this.withoutEmail) && !this.$v.buffer.$invalid;
        },
        isAddressEmpty: function () {
            return Object.values(this.userDetails.address).every(x => ( x === ''));
        }
    },
    watch: {
        value: {
            handler() {
                this.buffer = cloneObject(this.userDetails)
            },
            deep: true
        },
        isUserDetailRetrieved() {
            this.bufferIsUserDetailRetrieved = this.isUserDetailRetrieved;
        },
        shouldJobDetailBeRetrieved() {
            this.updateInput();
        }
    },
    mounted: function () {
        if (this.userDetails.user.email) {
            this.$v.buffer.user.email.$touch();
            this.bufferIsUserDetailRetrieved = true;
        }
        this.init();
    },
    updated: function () {
        this.init();
    },
    validations() {
        return {
            buffer: {
                user: {
                    title: this.enableValidation ? { required } : {},
                    firstName: this.enableValidation ? { required } : {},
                    surname: this.enableValidation ? { required } : {},
                    mobileNumber: this.enableValidation ? { required } : {},
                    email: this.enableValidation ? { required, email, notSimondsEmail } : {},
                },
                address: {
                    address1: this.requireAddress() ? { required } : {},
                    address2: {},
                    suburb: this.requireAddress() ? { required } : {},
                    state: this.requireAddress() ? { required } : {},
                    postcode: this.requireAddress() ? { required, integer } : {},
                },
                isPrimary: {}
            }
        };
    },
    methods: {
        updateInput() {
            if (this.withoutEmail) {
                this.buffer.user.email = createDummyCustomerEmail(this.buffer.user.firstName, this.buffer.user.surname);
            }
            return this.$emit('change', { userDetails: this.buffer, isValid: this.isFormValid });
        },

        updateIsPrimary(value) {
            this.buffer.isPrimary = value;
            this.updateInput();
        },

        updateWithoutEmailFlag(value) {
            this.withoutEmail = value;
        },

        retrieveUserDetails() {
            const self = this;
            const data = {
                email: this.userDetails.user.email
            };
            // Only retrieve emails when the email is valid.
            if (!this.$v.buffer.user.email.$invalid) {
                getUserDetailsByEmailRequest(data).then((response) => {
                    if (response.body.user && response.body.user.email.length > 0) {
                        self.bufferIsUserDetailRetrieved = true
                    }
                    copyData(response.body.user, this.buffer.user);
                    copyData(response.body.address, this.buffer.address);
                });
            }
        },

        enableUserDetailRetrieval() {
            this.bufferIsUserDetailRetrieved = false;
            this.updateInput();
        },

        requireAddress(){
            return (this.enableValidation && !this.isAddressEmpty) || this.isCreditCardPayment;
        },

        init() {
            const self = this;
            this.$nextTick(function () {
                self.updateInput();
            })
        },

        getEmailAddresses() {
            const self = this;
            const payload = {
                email: this.userDetails.user.email,
            };
            getEmailAddressesRequest(payload).then((response) => {
                self.emailAddresses = response.body.emailAddresses
            });
        },
    },
}
</script>
